/**
 * AI Center(V2) 대화방 선택 상태 (2026-10-07, aibot-v2-conversation-rooms).
 *
 * 전에는 chatApi.ts 의 모듈 변수 하나(v2ConversationId)가 대화방 id 를 들고 있었다.
 * 그러면 A 방에 보낸 답이 늦게 돌아왔을 때 그 사이 고른 B 방에 붙는다.
 * 그래서 "지금 고른 방" 과 "보낼 때 잡아 둔 방" 을 나눈다.
 *
 *   snapshot()      전송 직전에 찍는다 — 방 id + 세대(epoch)
 *   isCurrent(s)    답이 돌아왔을 때 아직 그 방 · 그 세대인가. 아니면 화면에 아무 효과도 내지 않는다
 *   adopt(s, id)    새 방의 첫 전송 — 서버가 준 id 를 받되, 그 사이 다른 방을 골랐으면 받지 않는다.
 *                   받았으면 새 스냅샷을 돌려준다. 이후 검사는 반드시 그것으로 한다 — 옛 스냅샷(id 없음)은
 *                   이제 현재가 아니다 (2026-10-08 회귀: 새 방 첫 답의 메뉴 열기가 빠지던 것)
 *
 * 세대는 방을 고르거나 새 대화를 시작할 때만 오른다. adopt 는 올리지 않는다.
 * 저장소(sessionStorage)에는 마지막 방 id 만 둔다. 대화 내용은 두지 않는다.
 * 의존이 없는 순수 모듈 — 테스트가 별칭 없이 돈다.
 */
import { create } from "zustand";

export interface RoomSnapshot {
  idConv: string | null;
  epoch: number;
}

interface ChatRoomState {
  selectedId: string | null;
  epoch: number;
  /** 방을 고른다. 세대가 오른다 */
  select: (idConv: string | null) => void;
  /** 새 대화. 세대가 오른다 */
  newRoom: () => void;
  snapshot: () => RoomSnapshot;
  isCurrent: (s: RoomSnapshot) => boolean;
  /** 새 방의 첫 답 — 세대가 같고 아직 방이 없을 때만 id 를 받는다. 받았으면 새 스냅샷, 아니면 null */
  adopt: (s: RoomSnapshot, idConv: string) => RoomSnapshot | null;
}

export const chatRoomStore = create<ChatRoomState>((set, get) => ({
  selectedId: null,
  epoch: 0,
  select: (idConv) => set((st) => ({ selectedId: idConv, epoch: st.epoch + 1 })),
  newRoom: () => set((st) => ({ selectedId: null, epoch: st.epoch + 1 })),
  snapshot: () => ({ idConv: get().selectedId, epoch: get().epoch }),
  isCurrent: (s) => s.epoch === get().epoch && s.idConv === get().selectedId,
  adopt: (s, idConv) => {
    const st = get();
    if (s.epoch !== st.epoch || st.selectedId !== null || s.idConv !== null) return null;
    set({ selectedId: idConv });
    return { idConv, epoch: st.epoch };
  },
}));

/* ------------------------------------------------------------------ 마지막 방 기억 (id 만) */

const STORAGE_PREFIX = "snotes.aibot.v2.room";

/** 회사 · 사용자별 키. 다른 계정으로 로그인하면 남의 방 id 를 보지 않는다 */
export function roomStorageKey(cdFirm: string | undefined, cdUser: string | undefined): string | null {
  if (!cdFirm || !cdUser) return null;
  return `${STORAGE_PREFIX}:${cdFirm}:${cdUser}`;
}

/** 같은 탭에서 새로고침했을 때만 되살린다. 새 탭은 빈 상태로 연다 */
export function rememberRoom(key: string | null, idConv: string | null): void {
  if (!key) return;
  try {
    if (idConv) window.sessionStorage.setItem(key, idConv);
    else window.sessionStorage.removeItem(key);
  } catch {
    /* 비공개 창 · 차단 — 기억 못 해도 대화는 된다 */
  }
}

export function recallRoom(key: string | null): string | null {
  if (!key) return null;
  try {
    const v = window.sessionStorage.getItem(key);
    return v && /^[0-9a-f-]{36}$/i.test(v) ? v : null;
  } catch {
    return null;
  }
}

export function forgetRoom(key: string | null): void {
  rememberRoom(key, null);
}
